import { SectionError, SectionLoading } from '@/components/section-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { formatBreakdownAxisLabel, formatCurrency } from './formatting';
import type { UsageBreakdownRow } from './types';

const breakdownConfig = {
  total_cost_usd: {
    label: 'Heildarkostnaður',
    color: 'var(--primary)',
  },
};

const BreakdownChart = ({
  title,
  description,
  rows,
  isLoading,
  isError,
  error,
  onRetry,
}: {
  title: string;
  description: string;
  rows: UsageBreakdownRow[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
}) => {
  const chartData = rows.slice(0, 8).map((row) => ({
    ...row,
    label: row.key || 'Óþekkt',
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <SectionLoading label={`Sæki ${title.toLowerCase()}`} />}
        {isError && <SectionError title={`${title} hlóðst ekki`} error={error} onRetry={onRetry} />}
        {!isLoading && !isError && chartData.length === 0 && (
          <div className="flex h-[280px] items-center justify-center rounded-md border border-dashed text-muted-foreground text-sm">
            Engin gögn fundust fyrir valið tímabil.
          </div>
        )}
        {!isLoading && !isError && chartData.length > 0 && (
          <ChartContainer config={breakdownConfig} className="h-[280px] w-full">
            <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid horizontal={false} />
              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => formatCurrency(Number(value))}
              />
              <YAxis
                dataKey="label"
                type="category"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={180}
                tickFormatter={(value) => formatBreakdownAxisLabel(String(value))}
              />
              <ChartTooltip
                allowEscapeViewBox={{ x: true, y: true }}
                content={<ChartTooltipContent valueFormatter={(value) => formatCurrency(Number(value))} />}
              />
              <Bar dataKey="total_cost_usd" fill="var(--color-total_cost_usd)" radius={4} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export const UsageBreakdownCharts = ({
  featureRows,
  modelRows,
  isFeatureLoading,
  isModelLoading,
  isFeatureError,
  isModelError,
  featureError,
  modelError,
  onFeatureRetry,
  onModelRetry,
}: {
  featureRows: UsageBreakdownRow[];
  modelRows: UsageBreakdownRow[];
  isFeatureLoading: boolean;
  isModelLoading: boolean;
  isFeatureError: boolean;
  isModelError: boolean;
  featureError: unknown;
  modelError: unknown;
  onFeatureRetry: () => void;
  onModelRetry: () => void;
}) => (
  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
    <BreakdownChart
      title="Tegund"
      description="Kostnaður eftir notkunartegund"
      rows={featureRows}
      isLoading={isFeatureLoading}
      isError={isFeatureError}
      error={featureError}
      onRetry={onFeatureRetry}
    />
    <BreakdownChart
      title="Líkan"
      description="Kostnaður eftir veitanda og líkani"
      rows={modelRows}
      isLoading={isModelLoading}
      isError={isModelError}
      error={modelError}
      onRetry={onModelRetry}
    />
  </div>
);
